import { useState, useEffect } from "react"
import { router } from "@inertiajs/react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Minus } from "lucide-react"
import MediaUploader from "./media_uploader"

const getInitials = (value) => {
  if (!value) return "U"
  const trimmed = value.includes("@") ? value.split("@")[0] : value
  const parts = trimmed.replace(/[^a-zA-Z0-9 ]/g, " ").trim().split(/\s+/)
  if (parts.length === 0) return "U"
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase()
}

const ThreadEditor = ({ 
  posts, 
  onUpdatePost, 
  onAddPost, 
  onRemovePost, 
  joinedContent, 
  initialContent, 
  saveUrl, 
  onSaveStart, 
  onSaveEnd, 
  assets = [], 
  onMediaChange, 
  mediaSelection,
  displayName,
  handleLabel,
  avatarUrl,
}) => {
  const initials = getInitials(displayName || handleLabel)
  const [saveTimeout, setSaveTimeout] = useState(null)

  useEffect(() => {
    if (!saveUrl || !onSaveStart || !onSaveEnd || typeof joinedContent === 'undefined' || typeof initialContent === 'undefined') {
      if (saveTimeout) clearTimeout(saveTimeout);
      return;
    }

    if (saveTimeout) clearTimeout(saveTimeout)

    if (joinedContent === initialContent) {
      return
    }

    const timeout = setTimeout(() => {
      onSaveStart()

      const data = { post: { content: joinedContent } }
      const options = {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => { /* Parent handles state */ },
        onError: () => { /* Parent handles state */ },
        onFinish: () => onSaveEnd()
      }

      router.patch(saveUrl, data, options)

    }, 750)

    setSaveTimeout(timeout)

    return () => {
      if (timeout) clearTimeout(timeout)
    }

  }, [joinedContent, initialContent, saveUrl, onSaveStart, onSaveEnd])

  return (
    <div className="space-y-4 rounded-lg p-4">
      {/* Avatar + Profile Info */}
      {(displayName || handleLabel) && (
        <div className="flex items-center gap-3">
          <Avatar>
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={displayName} />
            ) : (
              <AvatarFallback className="text-xs font-semibold">
                {initials}
              </AvatarFallback>
            )}
          </Avatar>
          <div>
            <div className="text-sm font-semibold">{displayName}</div>
            <div className="text-xs text-muted-foreground">@{handleLabel}</div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {posts.map((post, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              {posts.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemovePost(index)}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Textarea
              value={post}
              onChange={(e) => onUpdatePost(index, e.target.value)}
              placeholder={`What's happening?`}
              className="min-h-[100px] !border-0 !bg-transparent !ring-0 !ring-offset-0 focus-visible:!ring-0 resize-none"
            />
          </div>
        ))}
      </div>

      {/* Post Options Add media, create thread / split post, etc */}
      <div className="flex items-center gap-2">
        <MediaUploader
          assets={assets}
          onChange={onMediaChange}
          currentSelection={mediaSelection}
        />
        <button
          type="button"
          onClick={onAddPost}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Create thread
        </button>
      </div>
    </div>
  )
}

export default ThreadEditor
